import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../services/analytics';
import PublicPageFooter from '../components/PublicPageFooter';
import { useAuth } from '../hooks/useAuth';
import { ROUTES, API_BASE_URL } from '../config/constants';

// Same site-key/bypass scheme as LoginPage and ForgotPasswordPage.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_BYPASS =
  !RECAPTCHA_SITE_KEY || process.env.NODE_ENV === 'development';

interface FormDataState {
  name: string;
  email: string;
  companyName: string;
  phone: string;
  password: string;
  confirmPassword: string;
  // Mowilex specific fields
  mowilexCustomerId: string;
  territory: string;
  contactPerson: string;
  // Indonesian address fields
  address1: string;
  address2: string;
  city: string;
  province: string;
  kecamatan: string;
  kelurahan: string;
  postalCode: string;
  // Legacy address field for backward compatibility
  address: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  companyName?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  // Mowilex specific fields
  mowilexCustomerId?: string;
  territory?: string;
  contactPerson?: string;
  // Indonesian address fields
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  kecamatan?: string;
  kelurahan?: string;
  postalCode?: string;
  // Legacy address field for backward compatibility
  address?: string;
  recaptcha?: string;
  submit?: string;
}

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<FormDataState>({
    name: '',
    email: '',
    companyName: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Mowilex specific fields
    mowilexCustomerId: '',
    territory: '',
    contactPerson: '',
    // Indonesian address fields
    address1: '',
    address2: '',
    city: '',
    province: '',
    kecamatan: '',
    kelurahan: '',
    postalCode: '',
    // Legacy address field for backward compatibility
    address: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(
    RECAPTCHA_BYPASS ? 'dev-bypass' : null,
  );

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaToken(value);
    if (value && errors.recaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: undefined }));
    }
  };
  
  // Location dropdown data
  const [provinces, setProvinces] = useState<string[]>([]);
  const [kecamatanList, setKecamatanList] = useState<string[]>([]);
  const [kelurahanList, setKelurahanList] = useState<string[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(true);
  const [isLoadingKecamatan, setIsLoadingKecamatan] = useState(false);
  const [isLoadingKelurahan, setIsLoadingKelurahan] = useState(false);

  // API functions for fetching location data
  const fetchProvinces = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/regions/provinces`);
      if (response.ok) {
        const data = await response.json();
        setProvinces(data);
      } else {
        console.error('Failed to fetch provinces');
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
    } finally {
      setIsLoadingProvinces(false);
    }
  };

  const fetchKecamatan = async (province: string) => {
    if (!province) return;
    setIsLoadingKecamatan(true);
    try {
      const response = await fetch(`${API_BASE_URL}/regions/provinces/${encodeURIComponent(province)}/kecamatan`);
      if (response.ok) {
        const data = await response.json();
        setKecamatanList(data);
      } else {
        console.error('Failed to fetch kecamatan');
        setKecamatanList([]);
      }
    } catch (error) {
      console.error('Error fetching kecamatan:', error);
      setKecamatanList([]);
    } finally {
      setIsLoadingKecamatan(false);
    }
  };

  const fetchKelurahan = async (province: string, kecamatan: string) => {
    if (!province || !kecamatan) return;
    setIsLoadingKelurahan(true);
    try {
      const response = await fetch(`${API_BASE_URL}/regions/provinces/${encodeURIComponent(province)}/kecamatan/${encodeURIComponent(kecamatan)}/kelurahan`);
      if (response.ok) {
        const data = await response.json();
        setKelurahanList(data);
      } else {
        console.error('Failed to fetch kelurahan');
        setKelurahanList([]);
      }
    } catch (error) {
      console.error('Error fetching kelurahan:', error);
      setKelurahanList([]);
    } finally {
      setIsLoadingKelurahan(false);
    }
  };

  // Load provinces on component mount
  useEffect(() => {
    fetchProvinces();
  }, []);

  // Load kecamatan when province changes
  useEffect(() => {
    if (formData.province) {
      fetchKecamatan(formData.province);
      // Reset dependent fields when province changes
      setFormData(prev => ({ ...prev, kecamatan: '', kelurahan: '' }));
      setKelurahanList([]);
    } else {
      setKecamatanList([]);
      setKelurahanList([]);
    }
  }, [formData.province]);

  // Load kelurahan when kecamatan changes
  useEffect(() => {
    if (formData.province && formData.kecamatan) {
      fetchKelurahan(formData.province, formData.kecamatan);
      // Reset kelurahan when kecamatan changes
      setFormData(prev => ({ ...prev, kelurahan: '' }));
    } else {
      setKelurahanList([]);
    }
  }, [formData.kecamatan]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required', { field: t('auth.name') });
    }
    
    if (!formData.email) {
      newErrors.email = t('validation.required', { field: t('auth.email') });
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.email');
    }
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = t('validation.required', { field: t('auth.companyName') });
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('validation.required', { field: t('auth.phone') });
    } else {
      // Indonesian phone: +62xxx, 62xxx, 08xxx, or 8xxx (9-15 digits total)
      const cleaned = formData.phone.replace(/[\s\-()]/g, '');
      const idPhoneRegex = /^(\+62|62|0)?8[1-9]\d{6,11}$/;
      if (!idPhoneRegex.test(cleaned)) {
        newErrors.phone = t('validation.phone');
      }
    }
    
    // Mowilex Customer ID (optional but validate format if provided)
    if (formData.mowilexCustomerId && !/^[A-Z0-9.-]+$/.test(formData.mowilexCustomerId)) {
      newErrors.mowilexCustomerId = 'Please enter a valid Mowilex Customer ID (letters, numbers, dots, and dashes only)';
    }
    
    // Territory is required
    if (!formData.territory.trim()) {
      newErrors.territory = t('validation.required', { field: 'Territory' });
    }
    
    // Contact person is required
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = t('validation.required', { field: 'Contact Person' });
    }
    
    // Indonesian address fields validation
    if (!formData.address1.trim()) {
      newErrors.address1 = t('validation.required', { field: 'Primary Address' });
    }
    
    if (!formData.city.trim()) {
      newErrors.city = t('validation.required', { field: 'City' });
    }
    
    if (!formData.province.trim()) {
      newErrors.province = t('validation.required', { field: 'Province' });
    }
    
    if (!formData.kecamatan.trim()) {
      newErrors.kecamatan = t('validation.required', { field: 'Kecamatan' });
    }
    
    if (!formData.kelurahan.trim()) {
      newErrors.kelurahan = t('validation.required', { field: 'Kelurahan' });
    }
    
    if (!formData.postalCode.trim()) {
      newErrors.postalCode = t('validation.required', { field: 'Postal Code' });
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'Please enter a valid 5-digit postal code';
    }
    
    if (!formData.password) {
      newErrors.password = t('validation.required', { field: t('auth.password') });
    } else if (formData.password.length < 6) {
      newErrors.password = t('validation.minLength', { field: t('auth.password'), length: 6 });
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.required', { field: t('auth.confirmPassword') });
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMatch');
    }

    if (!recaptchaToken && !RECAPTCHA_BYPASS) {
      newErrors.recaptcha = t('auth.recaptchaRequired', {
        defaultValue: 'Silakan verifikasi bahwa Anda bukan robot',
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSelectChange = (name: string) => (e: any) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user selects
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const result = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName,
        contactPerson: formData.contactPerson,
        phone: formData.phone,
        // Mowilex-specific fields
        mowilexCustomerId: formData.mowilexCustomerId,
        territory: formData.territory,
        // Indonesian address fields
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        province: formData.province,
        kecamatan: formData.kecamatan,
        kelurahan: formData.kelurahan,
        postalCode: formData.postalCode,
        // Legacy address field for backward compatibility
        address: formData.address,
        recaptchaToken: recaptchaToken ?? undefined,
      });
      setRegistrationData(result);
      setShowSuccess(true);
      // GA event -- pairs with `magic_link_activated` (fired when
      // the new user completes activation from their email link) to
      // measure end-to-end signup conversion.
      trackEvent('signup_submitted');
    } catch (error: any) {
      const msg = error?.response?.data?.detail
        || error?.message
        || 'Registration failed. Please try again.';
      setErrors({ submit: msg });
      // Reset the widget — Google rejects a re-used token on re-submit.
      if (!RECAPTCHA_BYPASS) {
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccess) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center', width: '100%' }}>
            {/* Success Icon */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Typography variant="h2" color="white" sx={{ fontWeight: 'bold' }}>
                  ✓
                </Typography>
              </Box>
            </Box>

            {/* Success Message */}
            <Typography variant="h4" color="success.main" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('auth.registrationSubmitted')}
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
              {t('auth.thankYou')} <strong>{formData.name}</strong>! Your registration request has been submitted successfully.
            </Typography>

            {/* Registration Details */}
            {registrationData && (
              <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>{t('auth.requestId')}</strong> {registrationData.request_id}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Email:</strong> {formData.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('auth.estimatedReviewTime')}</strong> {registrationData.estimated_review_time || '2-3 business days'}
                </Typography>
              </Box>
            )}

            {/* What's Next */}
            <Box sx={{ mb: 4, textAlign: 'left' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('auth.whatHappensNext')}
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t('auth.reviewStep1')}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t('auth.reviewStep2')}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t('auth.reviewStep3')}
                </Typography>
              </Box>
            </Box>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate(ROUTES.LOGIN)}
                sx={{ mb: 1 }}
              >
                {t('auth.goToLogin')}
              </Button>
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => {
                  setShowSuccess(false);
                  setRegistrationData(null);
                  setFormData({
                    name: '',
                    email: '',
                    companyName: '',
                    phone: '',
                    password: '',
                    confirmPassword: '',
                    mowilexCustomerId: '',
                    territory: '',
                    contactPerson: '',
                    address1: '',
                    address2: '',
                    city: '',
                    province: '',
                    kecamatan: '',
                    kelurahan: '',
                    postalCode: '',
                    address: '',
                  });
                }}
              >
                {t('auth.registerAnother')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <img
              src="/Mowilex-Corporate.png"
              alt="Mowilex Logo"
              style={{
                maxWidth: '200px',
                height: 'auto',
                marginBottom: '16px'
              }}
            />
          </Box>
          <Typography component="h1" variant="h4" sx={{ mb: 2 }}>
            {t('auth.registerTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.registerSubtitle')}
          </Typography>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            {errors.submit && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.submit}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Basic Information Section */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {t('auth.basicInformation')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label={t('auth.fullName')}
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  autoComplete="name"
                  disabled={isLoading}
                />
                <TextField
                  required
                  fullWidth
                  id="email"
                  label={t('auth.emailAddress')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  autoComplete="email"
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label={t('auth.password')}
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  autoComplete="new-password"
                  disabled={isLoading}
                />
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t('auth.confirmPassword')}
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  disabled={isLoading}
                />
              </Box>

              {/* Company & Mowilex Information Section */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
                  {t('auth.companyInformation')}
                </Typography>
              </Box>
              
              <Box>
                <TextField
                  required
                  fullWidth
                  id="companyName"
                  label={t('auth.companyName')}
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                  autoComplete="organization"
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  fullWidth
                  id="mowilexCustomerId"
                  label={t('auth.customerIdOptional')}
                  name="mowilexCustomerId"
                  value={formData.mowilexCustomerId}
                  onChange={handleInputChange}
                  error={!!errors.mowilexCustomerId}
                  helperText={errors.mowilexCustomerId || "Enter your existing Mowilex Customer ID (e.g., A.L003)"}
                  placeholder="e.g., A.L003"
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  id="territory"
                  label={t('auth.territory')}
                  name="territory"
                  value={formData.territory}
                  onChange={handleInputChange}
                  error={!!errors.territory}
                  helperText={errors.territory}
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  id="contactPerson"
                  label={t('auth.contactPerson')}
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleInputChange}
                  error={!!errors.contactPerson}
                  helperText={errors.contactPerson}
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  id="phone"
                  label={t('auth.phoneNumber')}
                  name="phone"
                  placeholder="08xxxxxxxxxx"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={!!errors.phone}
                  helperText={errors.phone || 'e.g. 081234567890, +6281234567890'}
                  autoComplete="tel"
                  disabled={isLoading}
                />
              </Box>

              {/* Address Information Section */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
                  {t('auth.addressInformation')}
                </Typography>
              </Box>

              <Box>
                <TextField
                  required
                  fullWidth
                  id="address1"
                  label={t('auth.primaryAddress')}
                  name="address1"
                  value={formData.address1}
                  onChange={handleInputChange}
                  error={!!errors.address1}
                  helperText={errors.address1}
                  autoComplete="address-line1"
                  disabled={isLoading}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  id="address2"
                  label={t('auth.secondaryAddress')}
                  name="address2"
                  value={formData.address2}
                  onChange={handleInputChange}
                  error={!!errors.address2}
                  helperText={errors.address2}
                  autoComplete="address-line2"
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <FormControl fullWidth required error={!!errors.province}>
                  <InputLabel id="province-label">{t('auth.province')}</InputLabel>
                  <Select
                    labelId="province-label"
                    id="province"
                    value={formData.province}
                    label={t('auth.province')}
                    onChange={handleSelectChange('province')}
                    disabled={isLoading || isLoadingProvinces}
                  >
                    {isLoadingProvinces ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> {t('auth.loadingProvinces')}
                      </MenuItem>
                    ) : (
                      provinces.map((province) => (
                        <MenuItem key={province} value={province}>
                          {province}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.province && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 1 }}>
                      {errors.province}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  id="city"
                  label={t('auth.city')}
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  error={!!errors.city}
                  helperText={errors.city}
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <FormControl fullWidth required error={!!errors.kecamatan}>
                  <InputLabel id="kecamatan-label">Kecamatan</InputLabel>
                  <Select
                    labelId="kecamatan-label"
                    id="kecamatan"
                    value={formData.kecamatan}
                    label="Kecamatan"
                    onChange={handleSelectChange('kecamatan')}
                    disabled={isLoading || isLoadingKecamatan || !formData.province}
                  >
                    {isLoadingKecamatan ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> {t('auth.loadingKecamatan')}
                      </MenuItem>
                    ) : (
                      kecamatanList.map((kecamatan) => (
                        <MenuItem key={kecamatan} value={kecamatan}>
                          {kecamatan}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.kecamatan && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 1 }}>
                      {errors.kecamatan}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <FormControl fullWidth required error={!!errors.kelurahan}>
                  <InputLabel id="kelurahan-label">Kelurahan</InputLabel>
                  <Select
                    labelId="kelurahan-label"
                    id="kelurahan"
                    value={formData.kelurahan}
                    label="Kelurahan"
                    onChange={handleSelectChange('kelurahan')}
                    disabled={isLoading || isLoadingKelurahan || !formData.kecamatan}
                  >
                    {isLoadingKelurahan ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} /> {t('auth.loadingKelurahan')}
                      </MenuItem>
                    ) : (
                      kelurahanList.map((kelurahan) => (
                        <MenuItem key={kelurahan} value={kelurahan}>
                          {kelurahan}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.kelurahan && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 1 }}>
                      {errors.kelurahan}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <TextField
                  required
                  fullWidth
                  id="postalCode"
                  label={t('auth.postalCode')}
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode}
                  autoComplete="postal-code"
                  disabled={isLoading}
                />
              </Box>
            </Box>

            {!RECAPTCHA_BYPASS && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY!}
                  onChange={handleRecaptchaChange}
                />
              </Box>
            )}
            {errors.recaptcha && (
              <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                {errors.recaptcha}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || (!RECAPTCHA_BYPASS && !recaptchaToken)}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('auth.registerButton')
              )}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2">
                {t('auth.hasAccount')}{' '}
                <Link href={ROUTES.LOGIN} variant="body2">
                  {t('auth.loginHere')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
      <PublicPageFooter />
    </Container>
  );
};

export default RegisterPage;